<template>
    <div
        id="lt-yantr"
        class="advanced-calc auto-loan-calc">
        <div class="container-width">
            <div class="row autoloan-lite calculation-section">
                <label class="filter-header">{{ filterHeader }}</label>
                <div class="col-xs-12 col-sm-5 col-md-5 left-section autoloan-leftsection">
                    <ul class="filter-inputs one-col mb-none">
                        <li>
                            <number-input
                                v-model="LoanAmount"
                                :label="labelLoanAmount"
                                max-value="1000000"
                                precision="2"
                                @input="calculatePayment()"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="TermMonths"
                                :label="labelLoanTerm"
                                max-value="84"
                                symbol-aligned="none"
                                inputmode="decimal"
                                no-decimal="true"
                                @input="calculatePayment()"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="InterestRate"
                                :label="labelInterestRate"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                max-value="100.00"
                                inputmode="decimal"
                                @input="calculatePayment()"
                            />
                        </li>
                    </ul>
                </div>
                <div class="col-xs-12 col-sm-7 col-md-7 right-section text-center autoloan-rightsection">
                    <h4>Your estimated auto payment will be:</h4>
                    <label
                        class="homeprice"
                        :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(TotalMoPayment) }}</label>
                    <ul class="calc-box">
                        <li>
                            <label>Loan Amount:</label>
                            <h3 class="value">
                                ${{ currencyWholeNumber(TotalLoanAmount) }}
                            </h3>
                        </li>
                        <li>
                            <label>Total Payments:</label>
                            <h3 class="value">
                                {{ TermMonths }}
                            </h3>
                        </li>
                    </ul>
                    <div class="view-free-offers">
                        <h4>We've found offers for this result:</h4>
                        <button
                            class="btn btn-blue btn-arrow"
                            type="submit"
                            @click="redirectToUrl()">
                            {{ DestinationButtonText }}
                        </button>
                    </div>
                    <div>
                        <advertising-disclosures
                            v-if="DisclosureText"
                            :disclosure-text="DisclosureText"
                            :disclosure-tokens="DisclosureTokens"
                        />
                    </div>
                    <lt-logo v-if="ltLogo==1">
                        <small>NMLS#1136 Terms & Conditions Apply</small>
                    </lt-logo>
                </div>
                <div
                    v-if="howToUseCalculator==1"
                    class="col-xs-12 about-calculator text-center">
                    <p class="sub-heading">
                        Use this auto calculator to estimate your monthly auto payments, including estimates for taxes, and trade-ins.
                    </p>
                    <a v-vue-scroll-to="{ el:'#how-to-use-auto-loan-calculator', offset: -90 }">How to use this calculator</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import VueScrollTo from 'vue-scrollto';
    import NumberInput from '~/components/number-input.vue';
    import BaseMixin from '~/mixin/base-mixin';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import PaymentHelper from '~/helpers/payment';

    export default {
        components: {
            NumberInput,
            LtLogo,
            AdvertisingDisclosures,
        },
        directives: {
            VueScrollTo,
        },
        mixins: [BaseMixin],
        props: {
            filterHeader: {
                default: '',
            },
            labelLoanAmount: {
                default: 'Total Loan Amount:',
            },
            labelLoanTerm: {
                default: 'Term (Months):',
            },
            labelInterestRate: {
                default: 'Interest Rate (%):',
            },
            interestRate: {
                default: 2.5,
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '01,57,58,59,99,101',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            howToUseCalculator: {
                default: 0,
            },
            loanAmount: {
                default: 25000,
            },
            destinationLinkOrId: {
                default: 'wp-auto',
            },
            destinationButtonText: {
                default: 'Compare Loan Offers',
            },
            termMonths: {
                default: 48,
            },
        },
        data() {
            return {
                InterestRate: this.interestRate,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                LoanAmount: this.loanAmount,
                TermMonths: this.termMonths,
                TotalMoPayment: 0,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                TotalLoanAmount: 0,
            };
        },
        computed: {
            isNegativeAmount() {
                return this.TotalMoPayment >= 0;
            },
        },
        mounted() {
            this.calculatePayment();
        },
        methods: {
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            currencyWholeNumber(figure) {
                return figure > 0 ? Accounting.formatNumber(figure) : 0;
            },
            calculatePayment() {
                this.LoanAmount = this.LoanAmount > 1000000 ? 1000000 : this.LoanAmount;
                this.TermMonths = this.TermMonths > 84 ? 84 : this.TermMonths;
                this.InterestRate = this.InterestRate > 100.00 ? 100.00 : this.InterestRate;
                const interestRatePercantage = this.InterestRate / 100 / 12;
                this.TotalMoPayment = parseInt(this.TermMonths, 10) === 0 || this.TermMonths === ''
                    ? 0
                    : parseInt(this.InterestRate, 10) === 0
                        ? Accounting.unformat(this.LoanAmount) / this.TermMonths
                        : PaymentHelper.getMonthlyPrincipalAndIntreset(Accounting.unformat(this.LoanAmount), this.TermMonths, interestRatePercantage);
                this.TotalLoanAmount = Accounting.unformat(this.LoanAmount);
            },
            redirectToUrl() {
                this.DestinationLinkOrId = `${this.destinationLinkOrId}&RequestedLoanTermInMonths=${this.TermMonths}`;
                this.redirect();
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
